import React, { useEffect, useRef, useState } from "react";
import api, { fileUrl } from "../../../lib/api";
import { AdminButton, AdminInput, AdminTextarea, AdminCard } from "../AdminUI";
import { Save, Loader2, Upload, ImageOff, Facebook, Instagram, MessageCircle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const SOCIALS = [
    { key: "facebook", label: "Facebook", icon: Facebook, placeholder: "https://facebook.com/ihre-seite" },
    { key: "instagram", label: "Instagram", icon: Instagram, placeholder: "https://instagram.com/ihre-seite" },
    { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, placeholder: "https://wa.me/49170..." },
];

const SiteInfoTab = () => {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploadingKey, setUploadingKey] = useState(null);
    const fileRefs = useRef({});

    useEffect(() => {
        api.get("/site-info").then((r) => setData(r.data));
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            const res = await api.put("/site-info", data);
            setData(res.data);
            toast.success("Informationen gespeichert");
        } catch {
            toast.error("Fehler beim Speichern");
        } finally {
            setSaving(false);
        }
    };

    const set = (k, v) => setData({ ...data, [k]: v });

    const uploadQr = async (key, file) => {
        if (!file) return;
        setUploadingKey(key);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setData((d) => ({ ...d, [`${key}_qr`]: up.data.url }));
            toast.success("QR-Code hochgeladen");
        } catch { toast.error("Upload fehlgeschlagen"); }
        finally { setUploadingKey(null); if (fileRefs.current[key]) fileRefs.current[key].value = ""; }
    };

    if (!data) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-garden-green" /></div>;

    return (
        <div className="space-y-6 max-w-3xl" data-testid="site-info-tab">
            <AdminCard className="space-y-4">
                <h3 className="font-serif text-xl text-deep-green">Über Uns</h3>
                <AdminInput label="Titel" value={data.about_title} onChange={(e) => set("about_title", e.target.value)} data-testid="site-about-title" />
                <AdminTextarea label="Text" rows={6} value={data.about_text} onChange={(e) => set("about_text", e.target.value)} data-testid="site-about-text" />
                <AdminInput label="Hero Untertitel (Startseite)" value={data.hero_subtitle} onChange={(e) => set("hero_subtitle", e.target.value)} data-testid="site-hero-subtitle" />
            </AdminCard>

            <AdminCard className="space-y-4">
                <h3 className="font-serif text-xl text-deep-green">Kontakt</h3>
                <AdminInput label="Adresse" value={data.address} onChange={(e) => set("address", e.target.value)} data-testid="site-address" />
                <div className="grid sm:grid-cols-2 gap-4">
                    <AdminInput label="Telefon" value={data.phone} onChange={(e) => set("phone", e.target.value)} data-testid="site-phone" />
                    <AdminInput label="E-Mail" value={data.email} onChange={(e) => set("email", e.target.value)} data-testid="site-email" />
                </div>
            </AdminCard>

            <AdminCard className="space-y-4">
                <div>
                    <h3 className="font-serif text-xl text-deep-green">Social Media</h3>
                    <p className="text-sm text-deep-green/65 mt-1">
                        Link eintragen und optional einen QR-Code als Bild hochladen. QR-Codes werden auf der Webseite zum Scannen angezeigt.
                    </p>
                </div>

                {SOCIALS.map(({ key, label, icon: Icon, placeholder }) => (
                    <div key={key} className="border-t border-cream-dark pt-4 first:border-t-0 first:pt-0 space-y-3" data-testid={`social-${key}`}>
                        <div className="flex items-center gap-2">
                            <div className="w-9 h-9 rounded-full bg-leaf-green/30 grid place-items-center">
                                <Icon className="w-4 h-4 text-deep-green" />
                            </div>
                            <span className="font-medium text-deep-green">{label}</span>
                        </div>

                        <AdminInput
                            label="Link"
                            placeholder={placeholder}
                            value={data[key] || ""}
                            onChange={(e) => set(key, e.target.value)}
                            data-testid={`social-${key}-url`}
                        />

                        <div className="flex items-start gap-3">
                            {data[`${key}_qr`] ? (
                                <img src={fileUrl(data[`${key}_qr`])} alt={`${label} QR-Code`} className="w-24 h-24 rounded-xl object-cover border border-cream-dark bg-white" />
                            ) : (
                                <div className="w-24 h-24 rounded-xl bg-cream-dark/50 grid place-items-center border border-dashed border-cream-dark">
                                    <ImageOff className="w-6 h-6 text-deep-green/30" />
                                </div>
                            )}
                            <div className="flex flex-col gap-2 pt-1">
                                <label className="cursor-pointer">
                                    <input
                                        ref={(el) => (fileRefs.current[key] = el)}
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp"
                                        className="hidden"
                                        onChange={(e) => uploadQr(key, e.target.files?.[0])}
                                        data-testid={`social-${key}-qr-input`}
                                    />
                                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-cream-dark hover:bg-cream-dark/80 text-deep-green">
                                        {uploadingKey === key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                        {data[`${key}_qr`] ? "QR-Code ändern" : "QR-Code hochladen"}
                                    </span>
                                </label>
                                {data[`${key}_qr`] && (
                                    <AdminButton variant="ghost" onClick={() => set(`${key}_qr`, "")} className="w-fit">
                                        <ImageOff className="w-4 h-4" /> QR entfernen
                                    </AdminButton>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </AdminCard>

            <AdminCard className="space-y-4">
                <div>
                    <h3 className="font-serif text-xl text-deep-green">Sichtbarkeit der Bereiche</h3>
                    <p className="text-sm text-deep-green/65 mt-1">
                        Schalten Sie einzelne Bereiche auf der Startseite aus oder ein.
                    </p>
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                    {[
                        { key: "show_todays_ice", label: "Heutiges Softeis" },
                        { key: "show_promotions", label: "Aktionen" },
                        { key: "show_take_home", label: "Für Später" },
                        { key: "show_about", label: "Über Uns (Text)" },
                        { key: "show_opening_hours", label: "Öffnungszeiten" },
                        { key: "show_gallery", label: "Galerie" },
                        { key: "show_contact", label: "Kontakt-Karten" },
                        { key: "show_map", label: "Google Maps Karte" },
                        { key: "show_socials", label: "Social Media (QR-Codes)" },
                    ].map(({ key, label }) => {
                        const on = data[key] !== false;
                        return (
                            <button
                                key={key}
                                type="button"
                                onClick={() => set(key, !on)}
                                className={`flex items-center justify-between px-4 py-3 rounded-xl border transition ${
                                    on
                                        ? "bg-garden-green/10 border-garden-green/40 text-deep-green"
                                        : "bg-cream-dark/40 border-cream-dark text-deep-green/50"
                                }`}
                                data-testid={`visibility-${key}`}
                            >
                                <span className="font-medium text-sm">{label}</span>
                                <div className="flex items-center gap-2">
                                    <span className={`text-xs font-semibold ${on ? "text-garden-green-dark" : "text-deep-green/40"}`}>
                                        {on ? "AN" : "AUS"}
                                    </span>
                                    {on ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </AdminCard>

            <AdminCard className="space-y-4">
                <div>
                    <h3 className="font-serif text-xl text-deep-green">Impressum</h3>
                    <p className="text-sm text-deep-green/65 mt-1">
                        Wird auf der Seite <span className="font-mono text-xs bg-cream-dark px-1.5 py-0.5 rounded">/impressum</span> angezeigt.
                        Leerzeilen werden als Absätze übernommen.
                    </p>
                </div>
                <AdminTextarea
                    rows={14}
                    value={data.impressum}
                    onChange={(e) => set("impressum", e.target.value)}
                    placeholder={`Angaben gemäß § 5 TMG:\n\nSofteis Garten\nMax Mustermann\nHofackerstraße 31\n63776 Mömbris-Gunzenbach\n\nKontakt:\nTelefon: 0170 3093806\nE-Mail: info@softeisgarten.de\n\nUmsatzsteuer-ID:\n...`}
                    data-testid="site-impressum"
                />
            </AdminCard>

            <AdminButton onClick={save} loading={saving} data-testid="site-info-save">
                <Save className="w-4 h-4" /> Speichern
            </AdminButton>
        </div>
    );
};

export default SiteInfoTab;
